import { useEffect, useState } from "react";
import { useModal } from "../context/ModalContext";
import {
  getCompanyBranchById,
  saveCompanyBranch,
  updateCompanyBranch,
} from "../services/companyService";
import Loading from "./Loading";
import {
  Save,
  Loader2,
  ArrowLeft,
  ChevronsRight,
  Plus,
  Trash2,
} from "lucide-react";
import isEqual from "lodash/isEqual";
import { useToast } from "../context/ToastContext";
import FormNavigationButtons from "./FormNavigationButtons";

const CONTACT_TYPES = [
  { id: 1, value: "Official Contact Number" },
  { id: 2, value: "Contact Number" },
  { id: 3, value: "Telephone/Fax" },
  { id: 4, value: "Email" },
  { id: 5, value: "Official Email" },
];

const BRANCH_TYPES = [
  { id: 1, value: "Parent" },
  { id: 2, value: "Subsidiary" },
];

function FormButton({
  icon: Icon,
  children,
  onClick,
  disabled,
  variant = "primary",
  loading,
}) {
  const baseClasses =
    "flex items-center gap-2 px-6 py-2 rounded-lg transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed";
  const variants = {
    primary: "bg-blue-600 hover:bg-blue-700 text-white",
    success: "bg-green-600 hover:bg-green-700 text-white",
    secondary: "bg-gray-100 hover:bg-gray-200 text-gray-800",
    purple: "bg-purple-600 hover:bg-purple-700 text-white",
    danger: "bg-red-600 hover:bg-red-700 text-white",
  };

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`${baseClasses} ${variants[variant]}`}
    >
      {loading ? (
        <Loader2 className="animate-spin w-4 h-4" />
      ) : (
        Icon && <Icon className="w-4 h-4" />
      )}
      {children}
    </button>
  );
}

function BranchForm({ branch, index, onChange, onRemove, isEditable }) {
  const [contactErrors, setContactErrors] = useState([]);

  const handleFieldChange = (field, value) => {
    if (isEditable) {
      // Only allow change if not editable
      onChange({ ...branch, [field]: value });
    }
  };

  const handleAddressChange = (field, value) => {
    if (isEditable) {
      // Only allow change if not editable
      onChange({
        ...branch,
        address: {
          ...branch.address,
          [field]: value,
        },
      });
    }
  };

  const validateContactField = (contact, i) => {
    let error = "";
    if (contact.contactType === 4 || contact.contactType === 5) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(contact.value)) error = "Invalid email address.";
    } else {
      if (!/^[0-9]+$/.test(contact.value))
        error = "Contact number must be numeric.";
    }
    setContactErrors((prev) => {
      const updated = [...prev];
      updated[i] = error;
      return updated;
    });
  };

  const handleContactChange = (i, field, value) => {
    if (isEditable) {
      // Only allow change if not editable
      const updatedContacts = [...branch.contacts];
      updatedContacts[i][field] = value;
      onChange({ ...branch, contacts: updatedContacts });
      validateContactField(updatedContacts[i], i);
    }
  };

  const addContact = () => {
    if (isEditable) {
      // Only allow add if not editable
      onChange({
        ...branch,
        contacts: [...branch.contacts, { value: "", contactType: 1 }],
      });
      setContactErrors([...contactErrors, ""]);
    }
  };

  const removeContact = (i) => {
    if (isEditable) {
      // Only allow remove if not editable
      const updatedContacts = branch.contacts.filter((_, idx) => idx !== i);
      const updatedErrors = contactErrors.filter((_, idx) => idx !== i);
      onChange({ ...branch, contacts: updatedContacts });
      setContactErrors(updatedErrors);
    }
  };

  const inputClassName = `w-full mb-4 px-4 py-2 border rounded ${
    !isEditable ? "bg-gray-100" : ""
  }`;
  const selectClassName = `flex-grow px-4 py-2 border rounded ${
    !isEditable ? "bg-gray-100" : ""
  }`;

  const addressInputClassName = `px-4 py-2 border rounded ${
    !isEditable ? "bg-gray-100" : ""
  }`;

  const radioLabelClassName = `inline-flex items-center gap-2 ${
    !isEditable ? "cursor-not-allowed" : ""
  }`;

  return (
    <div className="bg-white mb-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-xl font-semibold">
          {branch.branchType === 1
            ? "Parent Branch"
            : `Subsidiary Branch ${index}`}
        </h3>
        {isEditable && branch.branchType === 2 && (
          <FormButton
            icon={Trash2}
            onClick={() => onRemove(index)}
            variant="danger"
            disabled={!isEditable}
          >
            Remove
          </FormButton>
        )}
      </div>

      <input
        className={inputClassName}
        value={branch.name}
        onChange={(e) => handleFieldChange("name", e.target.value)}
        placeholder="Branch Name"
        disabled={!isEditable}
      />
      <input
        className={inputClassName}
        value={branch.websiteUrl}
        onChange={(e) => handleFieldChange("websiteUrl", e.target.value)}
        placeholder="Website URL"
        disabled={!isEditable}
      />

      <label className="block font-medium mb-1">Branch Type</label>
      <div className="flex gap-4 mb-4">
        {BRANCH_TYPES.map((bt) => (
          <label key={bt.id} className={radioLabelClassName}>
            <input
              type="radio"
              name={`branchType-${index}`}
              value={bt.id}
              checked={branch.branchType === bt.id}
              onChange={() => handleFieldChange("branchType", bt.id)}
              disabled={!isEditable || (bt.id === 1 && index > 0)}
            />
            {bt.value}
          </label>
        ))}
      </div>

      <h4 className="font-medium mb-2">Address</h4>
      <div className="grid grid-cols-2 gap-4 mb-4">
        <input
          className={`col-span-2 ${addressInputClassName}`}
          value={branch.address.registeredAddress || ""}
          onChange={(e) =>
            handleAddressChange("registeredAddress", e.target.value)
          }
          placeholder="Registered Address"
          disabled={!isEditable}
        />
        <input
          className={addressInputClassName}
          value={branch.address.city || ""}
          onChange={(e) => handleAddressChange("city", e.target.value)}
          placeholder="City"
          disabled={!isEditable}
        />
        <input
          className={addressInputClassName}
          value={branch.address.state || ""}
          onChange={(e) => handleAddressChange("state", e.target.value)}
          placeholder="State"
          disabled={!isEditable}
        />
        <input
          className={addressInputClassName}
          value={branch.address.country || ""}
          onChange={(e) => handleAddressChange("country", e.target.value)}
          placeholder="Country"
          disabled={!isEditable}
        />
        <input
          className={addressInputClassName}
          value={branch.address.zipCode || ""}
          onChange={(e) => handleAddressChange("zipCode", e.target.value)}
          placeholder="Zip Code"
          disabled={!isEditable}
        />
      </div>

      <h4 className="font-medium mb-2">Contacts</h4>
      {branch.contacts.map((contact, i) => (
        <div key={i} className="mb-4">
          <div className="grid grid-cols-5 gap-4 items-center">
            <input
              className={`col-span-2 px-4 py-2 border rounded ${
                !isEditable ? "bg-gray-100" : ""
              }`}
              value={contact.value}
              onChange={(e) => handleContactChange(i, "value", e.target.value)}
              placeholder="Contact Value"
              disabled={!isEditable}
            />
            <div className="col-span-2 flex items-center">
              <select
                className={selectClassName}
                value={contact.contactType}
                onChange={(e) =>
                  handleContactChange(
                    i,
                    "contactType",
                    parseInt(e.target.value)
                  )
                }
                disabled={!isEditable}
              >
                {CONTACT_TYPES.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.value}
                  </option>
                ))}
              </select>
              {isEditable && (
                <button
                  type="button"
                  className="ml-2 px-3 py-2 bg-red-500 text-white rounded hover:bg-red-600"
                  onClick={() => removeContact(i)}
                  disabled={!isEditable}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
          {contactErrors[i] && (
            <p className="text-red-500 text-sm mt-1 ml-1">{contactErrors[i]}</p>
          )}
        </div>
      ))}
      {!isEditable && (
        <FormButton
          icon={Plus}
          onClick={addContact}
          variant="secondary"
          disabled={!isEditable}
        >
          Add Contact
        </FormButton>
      )}
    </div>
  );
}

function ParentBranchForm({
  companyId,
  onNext,
  onBack,
  versionId,
  isEditable,
  currentStep,
  totalSteps,
}) {
  const [branches, setBranches] = useState([]);
  const [initialBranches, setInitialBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { showModal } = useModal();
  const toast = useToast();
  const [fallbackUsed, setFallbackUsed] = useState(false);
  const [hasChanges, setHasChanged] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      let fetchVersion = versionId;
      let isFallback = false;
      let res = await getCompanyBranchById(companyId, fetchVersion);

      if ((!res || res.length === 0) && fetchVersion > 1) {
        fetchVersion = fetchVersion - 1;
        isFallback = true;
        setFallbackUsed(true);
        res = await getCompanyBranchById(companyId, fetchVersion);
      }

      let mappedBranches;
      if (Array.isArray(res) && res.length > 0) {
        mappedBranches = res.map((branch) => {
          const mapped = {
            // remove `id` if fallback
            ...(isFallback ? {} : { id: branch.id || 0 }),
            name: branch.name || "",
            websiteUrl: branch.websiteUrl || "",
            version: versionId,
            companyId: parseInt(companyId),
            branchType: branch.branchType?.id || 1,
            address: {
              registeredAddress:
                branch.branchAddresses?.[0]?.registeredAddress || "",
              country: branch.branchAddresses?.[0]?.country || "",
              state: branch.branchAddresses?.[0]?.state || "",
              city: branch.branchAddresses?.[0]?.city || "",
              zipCode: branch.branchAddresses?.[0]?.zipCode || "",
              addressType: branch.branchAddresses?.[0]?.addressType?.id || 0,
              ...(isFallback
                ? {}
                : { id: branch.branchAddresses?.[0]?.id || 0 }),
            },
            contacts: branch.contacts?.length
              ? branch.contacts.map((c) => ({
                  value: c.value || "",
                  contactType: c.contactType || 1,
                }))
              : [{ value: "", contactType: 1 }],
          };

          return mapped;
        });
      } else {
        mappedBranches = [
          {
            id: 0,
            name: "",
            websiteUrl: "",
            version: versionId,
            companyId: parseInt(companyId),
            branchType: 1,
            address: {
              registeredAddress: "",
              country: "",
              state: "",
              city: "",
              zipCode: "",
              addressType: 1,
            },
            contacts: [{ value: "", contactType: 1 }],
          },
        ];
      }
      setBranches(mappedBranches);
      setInitialBranches(mappedBranches);
    } catch (err) {
      console.error("Failed to fetch branch data", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [companyId]);

  const handleBranchChange = (index, updatedBranch) => {
    const updated = [...branches];
    updated[index] = updatedBranch;
    setBranches(updated);
  };

  const addBranch = () => {
    if (!isEditable) {
      setBranches((prev) => [
        ...prev,
        {
          id: 0,
          name: "",
          websiteUrl: "",
          version: versionId,
          companyId: parseInt(companyId),
          branchType: 2,
          address: {
            registeredAddress: "",
            country: "",
            state: "",
            city: "",
            zipCode: "",
            addressType: 1,
          },
          contacts: [{ value: "", contactType: 1 }],
        },
      ]);
    }
  };

  const removeBranch = (index) => {
    if (!isEditable) {
      setBranches((prev) => prev.filter((_, i) => i !== index));
    }
  };

  const handleSave = async (showModalAfterSave, goNext) => {
    try {
      setSaving(true);
      for (const branch of branches) {
        const payload = {
          ...branch,
          companyId: parseInt(companyId),
        };
        const res =
          branch.id > 0
            ? await updateCompanyBranch(payload)
            : await saveCompanyBranch(payload);
        if (!res || !res.status) throw new Error("Save failed");
      }
      setInitialBranches([...branches]);
      if (showModalAfterSave) {
        // showModal("Saved", "Branch information saved successfully.");
        toast.success("Branch information saved successfully.");
      }
      if (goNext && onNext) onNext();
    } catch (err) {
      console.error("Save error", err);
      // showModal("Save Failed", "An error occurred while saving branches.");
      toast.error("An error occurred while saving branches.");
    } finally {
      setSaving(false);
    }
  };

  //setHasChanged(!isEqual(branches, initialBranches));

  return loading ? (
    <Loading fullscreen={false} />
  ) : (
    <section className="px-10 py-5 max-w-6xl mx-auto bg-white">
      {branches.map((branch, idx) => (
        <BranchForm
          key={idx}
          index={idx}
          branch={branch}
          onChange={(updatedBranch) => {
            handleBranchChange(idx, updatedBranch);
            setHasChanged(true);
          }}
          onRemove={removeBranch}
          isEditable={isEditable}
        />
      ))}

      <div className="mb-4">
        {!isEditable && (
          <FormButton
            icon={Plus}
            onClick={addBranch}
            variant="secondary"
            disabled={isEditable}
          >
            Add Subsidiary Branch
          </FormButton>
        )}
      </div>

      {/* --- Buttons Section using the new component --- */}
      <FormNavigationButtons
        onBack={onBack}
        onNext={onNext}
        handleSaveOnly={() => handleSave(true, false)}
        handleSaveAndContinue={() => handleSave(true, true)}
        isEditable={isEditable}
        isChanged={hasChanges}
        saving={saving}
        fallbackUsed={fallbackUsed}
        currentStep={currentStep} // Pass current step
        totalSteps={totalSteps} // Pass total steps
      />
      {/* --- End Buttons Section --- */}

      {/* Buttons */}
      <div className="flex flex-wrap gap-4 justify-between sm:justify-end mt-8 hidden">
        <button
          type="button"
          onClick={onBack}
          disabled={saving}
          className="flex items-center gap-2 px-6 py-2 rounded-lg bg-gray-200 text-gray-800 hover:bg-gray-300 transition duration-200 cursor-pointer"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>

        {isEditable && (
          <button
            type="button"
            onClick={() => handleSave(true, false)}
            disabled={!hasChanges || saving}
            className={`flex items-center gap-2 px-6 py-2 rounded-lg transition duration-200 ${
              saving || !hasChanges
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-blue-600 hover:bg-blue-700 text-white cursor-pointer"
            }`}
          >
            {saving ? (
              <Loader2 className="animate-spin w-4 h-4" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            Save
          </button>
        )}

        {isEditable && (
          <button
            type="button"
            onClick={() => handleSave(true, true)}
            disabled={!hasChanges || saving}
            className={`flex items-center gap-2 px-6 py-2 rounded-lg transition duration-200 ${
              saving || !hasChanges
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-green-600 hover:bg-green-700 text-white cursor-pointer"
            }`}
          >
            {saving ? (
              <Loader2 className="animate-spin w-4 h-4" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            Save & Continue
          </button>
        )}

        <button
          type="button"
          onClick={onNext}
          disabled={saving || (hasChanges && !isEditable && fallbackUsed)}
          className={`flex items-center gap-2 px-6 py-2 rounded-lg transition duration-200 ${
            saving || (hasChanges && !isEditable && fallbackUsed)
              ? "bg-gray-300 cursor-not-allowed"
              : "bg-purple-600 hover:bg-purple-700 text-white cursor-pointer"
          }`}
        >
          <ChevronsRight className="w-4 h-4" />
          Next
        </button>
      </div>
    </section>
  );
}

export default ParentBranchForm;
